import { Component } from '@theme/component';
import { OverlayDrawer } from '@theme/overlay-drawer';
import { isValidEmail } from '@theme/utilities';

/**
 * @typedef {Object} NewsletterPopupRefs
 * @property {HTMLFormElement} [form]
 * @property {HTMLInputElement} [email]
 * @property {HTMLElement} [fields] Form fields, hidden once subscribed
 * @property {HTMLElement} [done] Confirmation, shown once subscribed
 * @property {HTMLElement} [message] Error message (role=status)
 * @property {HTMLButtonElement} [submit]
 */

/**
 * @typedef {Object} PopupMemory
 * @property {number} t Last time the popup was seen or closed
 * @property {boolean} [done] Subscribed: never show again
 */

/**
 * @typedef {Object} MailchimpResponse
 * @property {string} [result]
 * @property {string} [msg]
 */

const STORAGE_KEY = 'newsletter-popup';
const DAY_MS = 864e5;
/** Wait before trying again while a modal overlay is open */
const RETRY_MS = 4000;
/** A blocked or non-JSONP answer loads fine but never calls back: give up after this */
const MAILCHIMP_TIMEOUT_MS = 8000;
/** Matches the CSS fade-out before `hidden` is set */
const HIDE_DELAY_MS = 340;

/** @returns {PopupMemory | null} */
function readMemory() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

/** @param {PopupMemory} value */
function writeMemory(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Private mode: show again next visit
  }
}

/**
 * Newsletter popup: a non-modal card shown after `data-delay` seconds or half a page of scrolling, once.
 * Closed or ignored = quiet for `data-days` days, subscribed = never again. No scrim, no focus trap:
 * the page stays usable. Never shows over a modal overlay (menu, cart, filters): it tries again shortly.
 * Subscribes through Mailchimp's JSONP endpoint so the confirmation shows in the card.
 *
 * Markup: close button `on:click="/hide"`, form `ref="form" on:submit="/onSubmit"` with refs
 * `email`, `fields`, `done`, `message`, `submit`.
 *
 * @extends {Component<NewsletterPopupRefs>}
 */
class NewsletterPopup extends Component {
  #shown = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #showTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hideTimer;
  /** @type {(() => void) | null} Cancels the pending Mailchimp request */
  #cancelRequest = null;

  connectedCallback() {
    // Right after the header in the DOM, so keyboard and screen-reader users reach it first
    const header = document.querySelector('site-header') || document.querySelector('.site-header');
    if (header?.parentElement && header.nextElementSibling !== this) {
      // Theme editor re-render: the copy moved earlier is stale
      for (const stale of document.querySelectorAll(this.localName)) if (stale !== this) stale.remove();
      // Reconnects right away: initialisation runs on that second connection
      header.after(this);
      return;
    }
    super.connectedCallback();

    const memory = readMemory();
    const days = Number(this.dataset.days) || 14;
    const quiet = memory && (memory.done || Date.now() - memory.t < days * DAY_MS);
    if (this.#shown || quiet || /^\/(checkout|cart|account)/.test(location.pathname)) return;

    this.#showTimer = setTimeout(this.#show, (Number(this.dataset.delay) || 12) * 1000);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#showTimer);
    window.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('keydown', this.#onKeydown);
    this.#cancelRequest?.();
  }

  hide() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    document.removeEventListener('keydown', this.#onKeydown);
    clearTimeout(this.#hideTimer);
    this.#hideTimer = setTimeout(() => {
      this.hidden = true;
    }, HIDE_DELAY_MS);
    if (!readMemory()?.done) writeMemory({ t: Date.now() });
  }

  /** @param {SubmitEvent} event */
  onSubmit(event) {
    event.preventDefault();
    const { form, email, message, submit } = this.refs;
    if (!form || !email || !message || !submit) return;
    if (submit.dataset.done) {
      this.hide();
      return;
    }

    const translations = window.Theme.translations;
    if (!isValidEmail(email.value)) {
      message.textContent = translations.invalidEmail;
      email.focus();
      return;
    }
    submit.disabled = true;
    message.textContent = '';

    this.#subscribe(form)
      .then(() => this.#showDone())
      .catch((/** @type {unknown} */ error) => {
        message.textContent = error instanceof Error ? error.message : translations.newsletterError;
      })
      .finally(() => {
        submit.disabled = false;
      });
  }

  /**
   * Mailchimp only answers cross-origin through JSONP.
   * @param {HTMLFormElement} form
   * @returns {Promise<void>}
   */
  #subscribe(form) {
    const translations = window.Theme.translations;
    const callbackName = `mailchimpCallback${Date.now()}`;
    const callbacks = /** @type {Record<string, unknown>} */ (/** @type {unknown} */ (window));
    const params = new URLSearchParams(/** @type {any} */ (new FormData(form)));
    params.set('c', callbackName);
    const script = document.createElement('script');

    return new Promise((resolve, reject) => {
      /** @type {ReturnType<typeof setTimeout> | undefined} */
      let timer;
      const cleanup = () => {
        clearTimeout(timer);
        delete callbacks[callbackName];
        script.remove();
        this.#cancelRequest = null;
      };
      /** @param {string} text */
      const fail = (text) => {
        cleanup();
        reject(new Error(text));
      };

      callbacks[callbackName] = (/** @type {MailchimpResponse | undefined} */ response) => {
        if (response?.result === 'success') {
          cleanup();
          resolve();
          return;
        }
        const raw = String(response?.msg || '');
        fail(/already subscribed|già iscritt/i.test(raw) ? translations.newsletterAlreadySubscribed : translations.newsletterError);
      };
      script.onerror = () => fail(translations.connectionError);
      timer = setTimeout(() => fail(translations.connectionError), MAILCHIMP_TIMEOUT_MS);
      this.#cancelRequest = cleanup;

      script.src = `${form.action.replace('/post?', '/post-json?')}&${params.toString()}`;
      document.head.appendChild(script);
    });
  }

  #showDone() {
    const { fields, done, submit } = this.refs;
    writeMemory({ t: Date.now(), done: true });
    if (fields) fields.hidden = true;
    if (done) done.hidden = false;
    if (!submit) return;
    submit.dataset.done = '1';
    const label = submit.firstElementChild;
    if (label) label.textContent = window.Theme.translations.continueShopping;
    submit.focus({ preventScroll: true });
  }

  #show = () => {
    if (this.#shown) return;
    if (OverlayDrawer.active) {
      // Never over the menu, cart or filters: try again shortly
      clearTimeout(this.#showTimer);
      this.#showTimer = setTimeout(this.#show, RETRY_MS);
      return;
    }
    this.#shown = true;
    clearTimeout(this.#showTimer);
    // Seen counts as "not now": a non-modal card that is simply ignored must not come back on every page
    if (!readMemory()?.done) writeMemory({ t: Date.now() });
    window.removeEventListener('scroll', this.#onScroll);
    clearTimeout(this.#hideTimer);
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    document.addEventListener('keydown', this.#onKeydown);
  };

  #onScroll = () => {
    const height = document.documentElement.scrollHeight - window.innerHeight;
    if (height > 0 && window.scrollY / height > 0.5) this.#show();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') this.hide();
  };
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
